/** One selected value of a chip, with the id its icon is looked up by. */
export interface FilterChipValue {
  /** Lets the host draw an icon for it (a status colour, a profile picture). */
  id?: string;
  label: string;
}

/** One active filter, as a removable chip beside a page's Filters button. */
export interface FilterChip<K extends string = string> {
  key: K;
  /** The whole chip in words — "Assignee: Mari Tamm +2". Used for aria labels. */
  label: string;
  /** The part that may be truncated when the chip hits its width cap. */
  text: string;
  /** "+2" — how many more values are selected. Rendered outside the truncation. */
  more?: string;
  /** The filter type ("Status"), bold in the "+N more" list. */
  prefix?: string;
  /** Every selected value, listed in full in the "+N more" list. */
  values?: FilterChipValue[];
  onRemove: () => void;
}

interface MultiValueChipOptions {
  /** Shown instead of a value when none of the selected ones has a label. */
  selectedCount?: number;
}

/**
 * A multi-select dimension as a chip: "Prefix: first value" plus a "+N" for
 * the rest, kept apart so a long first value truncates without hiding the count.
 * When no selected value has a label (its option is not loaded), the chip says
 * how many are selected rather than "Prefix: " with nothing after it.
 */
export function multiValueChip<K extends string>(
  key: K,
  prefix: string,
  values: Array<string | FilterChipValue>,
  onRemove: () => void,
  { selectedCount }: MultiValueChipOptions = {}
): FilterChip<K> {
  const items = values.map((v) => (typeof v === "string" ? { label: v } : v));
  const fallback = selectedCount && selectedCount > 0 ? String(selectedCount) : null;
  const first = items[0]?.label ?? fallback;
  const text = first ? `${prefix}: ${first}` : prefix;
  const more = items.length > 1 ? `+${items.length - 1}` : undefined;
  return {
    key,
    text,
    more,
    prefix,
    values: items.length > 0 ? items : undefined,
    label: more ? `${text} ${more}` : text,
    onRemove,
  };
}

/**
 * A single-valued filter as a chip — never a "+N". `prefix` names the filter
 * type in the "+N more" list; the chip itself shows the value alone.
 */
export function singleValueChip<K extends string>(
  key: K,
  text: string,
  onRemove: () => void,
  prefix?: string
): FilterChip<K> {
  return {
    key,
    text,
    label: text,
    prefix,
    values: prefix ? [{ label: text }] : undefined,
    onRemove,
  };
}
